import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../../services/supabaseClient';

const ProgramMatchingPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const clientId = params.get('client') || '';
  const [client, setClient] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Common Disciplines List
  const COMMON_DISCIPLINES = [
    'Business & Management',
    'Computer Science & IT',
    'Engineering',
    'Medicine & Health Sciences',
    'Arts, Design & Architecture',
    'Social Sciences',
    'Natural Sciences',
    'Law',
    'Education',
    'Hospitality & Tourism',
    'Agriculture & Environment',
    'Humanities'
  ];

  // Candidate Profile State
  const [profile, setProfile] = useState({
    level: '',
    discipline: '',
    country: '',
    ielts: '',
    gpa: '',
    a_level_passes: '',
    maxTuition: ''
  });

  // Derived options for dropdowns
  const [options, setOptions] = useState({ universities: [], countries: [], levels: [], intakes: [], disciplines: [] });
  const [disciplineMode, setDisciplineMode] = useState('select'); // 'select' or 'custom'

  const [toast, setToast] = useState(null);
  const showToast = (type, text) => {
    setToast({ type, text });
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => setToast(null), 2500);
  };

  // Application Modal
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedIntake, setSelectedIntake] = useState('');
  const [selectedCase, setSelectedCase] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [cases, setCases] = useState([]);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);

      const promises = [
        clientId ? supabase.from('clients').select('*').eq('id', clientId).single() : Promise.resolve({ data: null }),
        supabase.from('programs').select('*, universities!inner(name, country)').order('name'),
        supabase.from('intakes').select('*').order('start_date', { ascending: true })
      ];

      if (clientId) {
        promises.push(supabase.from('cases').select('*').eq('client_id', clientId));
      } else {
        promises.push(Promise.resolve({ data: [] }));
      }

      const [{ data: c }, { data: progs }, { data: ints }, { data: clientCases }] = await Promise.all(promises);

      setClient(c || null);
      setPrograms(progs || []);
      setIntakes(ints || []);
      setCases(clientCases || []);

      // Pre-fill profile if client data exists (optional, basic mapping)
      if (c) {
        // This is a basic guess, ideally we map more fields if available in client profile
        // setProfile(prev => ({ ...prev, country: c.preferred_country || '' }));
      }

      // Extract filter options
      if (progs) {
        const unis = [...new Set(progs.map(p => p.universities?.name).filter(Boolean))].sort();
        const countries = [...new Set(progs.map(p => p.universities?.country).filter(Boolean))].sort();
        const levels = [...new Set(progs.map(p => p.level).filter(Boolean))].sort();

        // Merge DB disciplines with Common ones
        const dbDisciplines = progs.map(p => p.discipline).filter(Boolean);
        const allDisciplines = [...new Set([...COMMON_DISCIPLINES, ...dbDisciplines])].sort();

        setOptions(prev => ({ ...prev, universities: unis, countries: countries, levels: levels, disciplines: allDisciplines }));
      }
      if (ints) {
        const terms = [...new Set(ints.map(i => i.term).filter(Boolean))].sort();
        setOptions(prev => ({ ...prev, intakes: terms }));
      }

    } catch (e) {
      console.error(e);
      setError('Failed to load matching data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [clientId]);

  const openApplicationModal = (program) => {
    setSelectedProgram(program);
    setSelectedCase('');
    setSelectedIntake('');
    setIsModalOpen(true);
  };

  const createApplication = async () => {
    try {
      if (!selectedProgram) return;
      if (!selectedIntake) return showToast('error', 'Please select an intake');
      if (!selectedCase) return showToast('error', 'Please select a case');

      const payload = {
        client_id: clientId || null,
        program_id: selectedProgram.id,
        intake_id: selectedIntake,
        case_id: selectedCase,
        status: 'Pending Application'
      };

      const { error } = await supabase.from('uni_applications').insert([payload]);
      if (error) throw error;

      showToast('success', 'Application created successfully');
      setIsModalOpen(false);
    } catch (e) {
      console.error(e);
      showToast('error', 'Failed to create application');
    }
  };

  // Helper to parse numeric requirements
  const parseRequirement = (val) => {
    if (!val) return null;
    const num = parseFloat(val);
    return isNaN(num) ? null : num;
  };

  // Matching Engine Logic
  const matchedPrograms = useMemo(() => {
    if (!programs.length) return [];

    return programs.map(p => {
      let score = 100;
      const reasons = [];
      let isMatch = true;

      // 1. Country (Hard Constraint if selected)
      if (profile.country && p.universities?.country !== profile.country) {
        score = 0;
        isMatch = false;
        reasons.push('Country mismatch');
      }

      // 2. Level (Hard Constraint if selected)
      if (profile.level && p.level !== profile.level) {
        score = 0;
        isMatch = false;
        reasons.push('Level mismatch');
      }

      // 3. Discipline (Soft Constraint / Keyword Match)
      if (profile.discipline) {
        const d = (p.discipline || '').toLowerCase();
        const cs = (p.course_of_study || '').toLowerCase();
        const search = profile.discipline.toLowerCase();

        // Use fuzzy match for flexibility
        if (!d.includes(search) && !cs.includes(search)) {
          // If totally irrelevant, filter out or heavily penalize
          score = 0;
          isMatch = false;
          reasons.push('Discipline mismatch');
        } else {
          // Match found
        }
      }

      // 4. IELTS (Requirement)
      if (profile.ielts && p.min_ielts) {
        const req = parseRequirement(p.min_ielts);
        const cand = parseFloat(profile.ielts);
        if (req && !isNaN(cand)) {
          if (cand >= req) {
            // Pass
          } else if (cand >= req - 0.5) {
            // Near miss
            score -= 20;
            reasons.push(`IELTS slightly low (Req: ${req})`);
          } else {
            // Fail
            score = 0;
            isMatch = false;
            reasons.push(`IELTS too low (Req: ${req})`);
          }
        }
      }

      // 5. GPA (Requirement)
      // Only check GPA if Master's or if user input GPA
      if (profile.gpa && p.min_gpa) {
        const req = parseRequirement(p.min_gpa);
        const cand = parseFloat(profile.gpa);
        if (req && !isNaN(cand)) {
          if (cand >= req) {
            // Pass
          } else if (cand >= req - 0.2) {
            // Near miss
            score -= 20;
            reasons.push(`GPA slightly low (Req: ${req})`);
          } else {
            // Fail
            score = 0;
            isMatch = false;
            reasons.push(`GPA too low (Req: ${req})`);
          }
        }
      }

      // 6. A Level Passes (Requirement)
      if (profile.a_level_passes && p.min_al_passes) {
        const req = parseRequirement(p.min_al_passes);
        const cand = parseFloat(profile.a_level_passes);
        if (req && !isNaN(cand)) {
          if (cand < req) {
            score = 0;
            isMatch = false;
            reasons.push(`Insufficient A-Level passes (Req: ${req})`);
          }
        }
      }

      // 7. Tuition (Filter)
      if (profile.maxTuition && p.tuition) {
        const max = parseFloat(profile.maxTuition);
        if (p.tuition > max) {
          score = 0;
          isMatch = false;
          reasons.push('Tuition too high');
        }
      }

      return { ...p, matchScore: Math.max(0, score), matchReasons: reasons, isMatch };
    })
      .filter(p => p.isMatch) // Only show matches (or near matches if we adjusted logic)
      .sort((a, b) => b.matchScore - a.matchScore); // Rank by score
  }, [programs, profile]);


  if (loading) return (
    <div className="min-h-screen bg-gray-900 font-mono text-white flex flex-col items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
      <div className="text-gray-400">Loading matching engine...</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-900 font-mono text-white p-6">
      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white">🎓 Program Matching Engine</h1>
          <p className="text-gray-400">
            {client ? `Matching for: ${client.first_name} ${client.last_name}` : 'Enter candidate details to find suitable programs'}
          </p>
        </div>
        <button onClick={() => navigate('/admin/clients')} className="text-gray-400 hover:text-white">
          Exit
        </button>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded border shadow-lg ${toast.type === 'success' ? 'bg-green-900/90 border-green-800 text-green-400' : 'bg-red-900/90 border-red-800 text-red-400'}`}>
          {toast.text}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">

        {/* Left Sidebar: Candidate Profile Inputs */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-gray-800 rounded-lg shadow border border-gray-700 p-5">
            <h3 className="text-lg font-bold mb-4 text-white flex items-center gap-2">
              👤 <span>Candidate Profile</span>
            </h3>

            <div className="space-y-4">
              {/* Country Preference */}
              <div>
                <label className="block text-xs text-gray-400 mb-1">Preferred Country</label>
                <select
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-500 text-sm"
                  value={profile.country}
                  onChange={(e) => setProfile({ ...profile, country: e.target.value })}
                >
                  <option value="">Any Country</option>
                  {options.countries.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              {/* Course Level */}
              <div>
                <label className="block text-xs text-gray-400 mb-1">Course Level</label>
                <select
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-500 text-sm"
                  value={profile.level}
                  onChange={(e) => setProfile({ ...profile, level: e.target.value })}
                >
                  <option value="">Any Level</option>
                  {options.levels.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>

              {/* Discipline */}
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="block text-xs text-gray-400">Discipline / Field</label>
                  {disciplineMode === 'custom' && (
                    <button
                      onClick={() => { setDisciplineMode('select'); setProfile({ ...profile, discipline: '' }); }}
                      className="text-[10px] text-blue-400 hover:text-blue-300"
                    >
                      Back to List
                    </button>
                  )}
                </div>

                {disciplineMode === 'select' ? (
                  <select
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-500 text-sm"
                    value={profile.discipline}
                    onChange={(e) => {
                      if (e.target.value === 'OTHER_CUSTOM') {
                        setDisciplineMode('custom');
                        setProfile({ ...profile, discipline: '' });
                      } else {
                        setProfile({ ...profile, discipline: e.target.value });
                      }
                    }}
                  >
                    <option value="">Any Discipline</option>
                    {options.disciplines.map(d => <option key={d} value={d}>{d}</option>)}
                    <option value="OTHER_CUSTOM" className="font-bold text-blue-300">+ Custom / Other</option>
                  </select>
                ) : (
                  <input
                    autoFocus
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="Type custom discipline..."
                    value={profile.discipline}
                    onChange={(e) => setProfile({ ...profile, discipline: e.target.value })}
                  />
                )}
              </div>

              <div className="border-t border-gray-700 pt-4">
                <h4 className="text-sm font-semibold text-gray-300 mb-3">Academic Scores</h4>

                {/* IELTS */}
                <div className="mb-3">
                  <label className="block text-xs text-gray-400 mb-1">IELTS Score</label>
                  <input
                    type="number"
                    step="0.5"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="e.g. 6.5"
                    value={profile.ielts}
                    onChange={(e) => setProfile({ ...profile, ielts: e.target.value })}
                  />
                </div>

                {/* GPA */}
                <div className="mb-3">
                  <label className="block text-xs text-gray-400 mb-1">GPA (if Masters)</label>
                  <input
                    type="number"
                    step="0.1"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="e.g. 3.0"
                    value={profile.gpa}
                    onChange={(e) => setProfile({ ...profile, gpa: e.target.value })}
                  />
                </div>

                {/* A Levels */}
                <div className="mb-3">
                  <label className="block text-xs text-gray-400 mb-1">A Level Passes</label>
                  <input
                    type="number"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="e.g. 3"
                    value={profile.a_level_passes}
                    onChange={(e) => setProfile({ ...profile, a_level_passes: e.target.value })}
                  />
                </div>
              </div>

              <div className="border-t border-gray-700 pt-4">
                {/* Tuition */}
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Max Tuition</label>
                  <input
                    type="number"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="e.g. 15000"
                    value={profile.maxTuition}
                    onChange={(e) => setProfile({ ...profile, maxTuition: e.target.value })}
                  />
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Right Content: Results */}
        <div className="lg:col-span-3">
          <div className="bg-gray-800 border border-gray-700 rounded-lg shadow h-full flex flex-col">
            <div className="p-4 border-b border-gray-700 flex justify-between items-center bg-gray-800/50">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                📋 <span>Matched Programs ({matchedPrograms.length})</span>
              </h3>
              <div className="text-xs text-gray-400">
                Sorted by Match %
              </div>
            </div>

            <div className="overflow-x-auto flex-1">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr>
                    <th className="bg-gray-900 p-3 border-b border-gray-700 text-xs font-medium text-gray-400 uppercase w-16">Match</th>
                    <th className="bg-gray-900 p-3 border-b border-gray-700 text-xs font-medium text-gray-400 uppercase">Program</th>
                    <th className="bg-gray-900 p-3 border-b border-gray-700 text-xs font-medium text-gray-400 uppercase">University</th>
                    <th className="bg-gray-900 p-3 border-b border-gray-700 text-xs font-medium text-gray-400 uppercase">Requirements</th>
                    <th className="bg-gray-900 p-3 border-b border-gray-700 text-xs font-medium text-gray-400 uppercase text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {matchedPrograms.map(p => {
                    const nextIntake = intakes.find(i => i.program_id === p.id);

                    // Color code match score
                    let scoreColor = 'text-green-400';
                    if (p.matchScore < 80) scoreColor = 'text-yellow-400';
                    if (p.matchScore < 50) scoreColor = 'text-red-400';

                    return (
                      <tr key={p.id} className="hover:bg-gray-700/50 transition-colors">
                        <td className="p-3 border-b border-gray-700 align-top">
                          <div className={`text-lg font-bold ${scoreColor}`}>
                            {p.matchScore}%
                          </div>
                          {p.matchReasons.length > 0 && (
                            <div className="text-[10px] text-yellow-500 mt-1 leading-tight">
                              {p.matchReasons.map((r, idx) => <div key={idx}>⚠️ {r}</div>)}
                            </div>
                          )}
                        </td>
                        <td className="p-3 border-b border-gray-700 align-top">
                          <div className="font-bold text-white text-sm">{p.name}</div>
                          <div className="text-xs text-gray-400">{p.discipline}</div>
                          {p.course_of_study && <div className="text-xs text-blue-400 mt-0.5">{p.course_of_study}</div>}
                          <div className="mt-2 text-xs text-emerald-400">
                            {p.tuition ? `${p.tuition} ${p.currency}` : 'Tuition N/A'}
                          </div>
                        </td>
                        <td className="p-3 border-b border-gray-700 align-top">
                          <div className="text-gray-300 text-sm">{p.universities?.name}</div>
                          <div className="text-xs text-gray-500">{p.universities?.country}</div>
                          <span className="inline-block mt-1 px-1.5 py-0.5 rounded text-[10px] font-medium border bg-blue-900/20 text-blue-400 border-blue-800">
                            {p.level}
                          </span>
                        </td>
                        <td className="p-3 border-b border-gray-700 align-top text-xs space-y-1">
                          <div className="flex justify-between gap-2">
                            <span className="text-gray-500">IELTS:</span>
                            <span className={p.min_ielts && parseFloat(profile.ielts) >= parseFloat(p.min_ielts) ? 'text-green-400' : 'text-gray-300'}>
                              {p.min_ielts || '-'}
                            </span>
                          </div>
                          <div className="flex justify-between gap-2">
                            <span className="text-gray-500">GPA:</span>
                            <span className={p.min_gpa && parseFloat(profile.gpa) >= parseFloat(p.min_gpa) ? 'text-green-400' : 'text-gray-300'}>
                              {p.min_gpa || '-'}
                            </span>
                          </div>
                          <div className="flex justify-between gap-2">
                            <span className="text-gray-500">A-Levels:</span>
                            <span className={p.min_al_passes && parseFloat(profile.a_level_passes) >= parseFloat(p.min_al_passes) ? 'text-green-400' : 'text-gray-300'}>
                              {p.min_al_passes || '-'}
                            </span>
                          </div>
                        </td>
                        <td className="p-3 border-b border-gray-700 text-right align-top">
                          <button
                            onClick={() => openApplicationModal(p)}
                            className="px-3 py-1.5 rounded text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                          >
                            Select
                          </button>
                          {nextIntake && (
                            <div className="mt-2 text-[10px] text-gray-400">
                              Next: {nextIntake.term}
                            </div>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                  {matchedPrograms.length === 0 && (
                    <tr>
                      <td colSpan={5} className="p-12 text-center text-gray-500">
                        <div className="text-4xl mb-2">🔍</div>
                        <p>No matching programs found.</p>
                        <p className="text-sm mt-1">Try adjusting the candidate profile or relaxing constraints.</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Application Modal */}
      {isModalOpen && selectedProgram && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-white mb-4">Create Application</h3>
            <div className="mb-4">
              <div className="text-sm text-gray-400 mb-1">Program</div>
              <div className="text-white font-medium">{selectedProgram.name}</div>
              <div className="text-sm text-blue-400">{selectedProgram.universities?.name}</div>
            </div>

            <div className="mb-4">
              <label className="block text-gray-400 text-sm mb-2">Select Case</label>
              <select
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-500"
                value={selectedCase}
                onChange={(e) => setSelectedCase(e.target.value)}
              >
                <option value="">Select a Case</option>
                {cases.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.title || 'Untitled Case'} - {c.status}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-6">
              <label className="block text-gray-400 text-sm mb-2">Select Intake</label>
              <select
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-500"
                value={selectedIntake}
                onChange={(e) => setSelectedIntake(e.target.value)}
              >
                <option value="">Select an Intake</option>
                {intakes.filter(i => i.program_id === selectedProgram?.id).map(i => (
                  <option key={i.id} value={i.id}>{i.term} (Starts: {i.start_date})</option>
                ))}
              </select>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 text-gray-300 hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={createApplication}
                disabled={!selectedIntake || !selectedCase}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Create Application
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ProgramMatchingPage;
